import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_THEME_ID, THEMES } from '@/themes';

const THEME_KEY = 'darkfeld-theme';
const SCALE_KEY = 'darkfeld-scale';

/** Valid UI scale steps (percentage of default 16 px root font-size). */
export const UI_SCALE_STEPS = [90, 100, 110, 120, 130, 140, 150] as const;
export type UiScale = (typeof UI_SCALE_STEPS)[number];
export const DEFAULT_UI_SCALE: UiScale = 100;

/** Read theme from localStorage, validate it exists, fall back to default. */
function readStoredTheme(): string {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored && THEMES.some((t) => t.id === stored)) return stored;
  } catch {
    // localStorage may be unavailable (e.g. private browsing restrictions)
  }
  return DEFAULT_THEME_ID;
}

/** Read UI scale from localStorage, validate it is a known step, fall back to 100. */
function readStoredScale(): UiScale {
  try {
    const stored = localStorage.getItem(SCALE_KEY);
    if (stored) {
      const n = parseInt(stored, 10);
      if ((UI_SCALE_STEPS as readonly number[]).includes(n)) return n as UiScale;
    }
  } catch { /* non-fatal */ }
  return DEFAULT_UI_SCALE;
}

interface ThemeState {
  themeId: string;
  uiScale: UiScale;
  previewActive: boolean;
  savedThemeId?: string;
  savedUiScale?: UiScale;
}

const initialState: ThemeState = {
  themeId: readStoredTheme(),
  uiScale:  readStoredScale(),
  previewActive: false,
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<string>) {
      state.themeId = action.payload;
      if (state.previewActive) return;
      try {
        localStorage.setItem(THEME_KEY, action.payload);
      } catch {
        // non-fatal — theme applies for the session even if storage fails
      }
    },
    setUiScale(state, action: PayloadAction<UiScale>) {
      state.uiScale = action.payload;
      if (state.previewActive) return;
      try {
        localStorage.setItem(SCALE_KEY, String(action.payload));
      } catch { /* non-fatal */ }
    },
    setPreviewPreferenceMode(state, action: PayloadAction<boolean>) {
      const active = action.payload;
      if (active && !state.previewActive) {
        state.savedThemeId = state.themeId;
        state.savedUiScale = state.uiScale;
        state.themeId = DEFAULT_THEME_ID;
        state.uiScale = DEFAULT_UI_SCALE;
        state.previewActive = true;
        return;
      }
      if (!active && state.previewActive) {
        state.themeId = state.savedThemeId ?? readStoredTheme();
        state.uiScale = state.savedUiScale ?? readStoredScale();
        state.savedThemeId = undefined;
        state.savedUiScale = undefined;
        state.previewActive = false;
      }
    },
  },
});

export const { setTheme, setUiScale, setPreviewPreferenceMode } = themeSlice.actions;
export default themeSlice.reducer;
